import { Box, VStack } from "@chakra-ui/react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { Button, Empty, Radio, Space, Table } from "antd";
import { ColumnsType } from "antd/lib/table";
import { StaffRoutes } from "appRoutePaths";
import SortableTable from "components/SortableTable";
import BatchAssignmentModal from "components/staff/Shipping/BatchAssignmentModal";
import BatchReassignmentModal from "components/staff/Shipping/BatchReassignmentModal";
import { useAuthenticatedRequest, useAuthenticatedRequestCreator } from "hooks/useRequests";
import useShipmentBatches from "hooks/useShipmentBatches";
import { InitialPageRequest, PageRequest } from "models/common";
import { ShipmentBatch, ShipmentBatchPuller, ShipmentBatchType, ShipmentBatchTypeNames, ShipmentCardStatus } from "models/shipping";
import { forwardRef, useCallback, useImperativeHandle, useRef, useState } from "react";
import { localizedDate } from "util/helpers";
import { StatusIcon } from "./ShipmentBatchCards";

const ViewByBatch = 0;
const ViewByPuller = 1;

type BatchTableRef = {
    refresh: () => Promise<void>;
}

const ByBatchesTable = forwardRef<BatchTableRef, {}>((_, ref) => {
    const [pageRequest, setPageRequest] = useState<PageRequest>({
        ...InitialPageRequest,
        filterBy: { ToBePulled: [''] }
    });
    const { isLoading, data, refetch } = useShipmentBatches(pageRequest);
    const { data: records, totalRecords, pageSize } = data || {};

    useImperativeHandle(ref, () => ({
        refresh: async () => { await refetch(); }
    }));

    const columns: ColumnsType<ShipmentBatch> = [
        {
            title: "Name",
            dataIndex: "name",
        },
        {
            title: "Assigned To",
            dataIndex: "assignedPullerName",
            render: (_, rec) => rec.assignedPullerId === null
                ? "N/A"
                : <BatchReassignmentModal
                    key={rec.id}
                    batchId={rec.id}
                    batchName={rec.name}
                    assignedUserId={rec.assignedPullerId}
                    assignedName={rec.assignedPullerName!}
                    loading={isLoading}
                    onReassignment={async () => { await refetch(); }}
                />
        },
        {
            title: "Generated",
            dataIndex: "generated",
            render: localizedDate
        },
        {
            title: "Type",
            dataIndex: "type",
            render: (t: ShipmentBatchType) => ShipmentBatchTypeNames[t]
        },
        {
            title: "Orders",
            dataIndex: "orders",
        },
        {
            title: "Items",
            dataIndex: "items",
            render: (value, rec) => <Space>
                {value}
                {rec.hasShipmentsWithMissingCards && <Button
                    type="link"
                    icon={<StatusIcon status={ShipmentCardStatus.NotPulled} />}
                    href={StaffRoutes.shipmentBatchMissingCards.url(rec.id)}
                />}
            </Space>
        },
        {
            title: "Pulled",
            dataIndex: "pulled",
            render: (_, rec) => `${((rec.pulled / rec.items) * 100).toFixed(2)}%`
        }
    ];

    const tableChanged = (
        // pagination
        { current: newPage }: any,
        // filtering
        _: any,
        // sorting
        __: any
    ) => {
        setPageRequest({ ...pageRequest, page: newPage });
    };

    return <Table
        id="shipment-batches-table"
        rowKey={r => r.id}
        columns={columns}
        dataSource={records}
        loading={isLoading}
        onChange={tableChanged}
        pagination={{
            total: totalRecords,
            current: pageRequest.page,
            pageSize,
            showSizeChanger: false,
            showQuickJumper: true,
            hideOnSinglePage: true
        }}
    />;
});

const ByPullerTable = forwardRef<BatchTableRef, {}>((_, ref) => {
    const { data = [], isLoading, refetch } = useQuery(
        ["shipment-batches", "by-puller"],
        useAuthenticatedRequest<ShipmentBatchPuller[]>({
            method: "get",
            url: "/shipping/batches/by-puller"
        })
    );

    const { mutateAsync: reorderAssignments, isLoading: isUpdating } = useMutation(
        useAuthenticatedRequestCreator<void, { userId: string, shipmentBatchIds: Record<string, number> }>(
            (data) => ({
                method: "post",
                url: "/shipping/batches/reorder",
                data
            })
        )
    );

    const styledRow = useCallback((value: any) => ({
        props: {
            style: { backgroundColor: 'antiquewhite' }
        },
        children: <div style={{ fontWeight: 'bold' }}>{value}</div>
    }), []);

    const columns: ColumnsType<ShipmentBatchPuller> = [
        {
            title: "Name", dataIndex: "name", render: styledRow,
        },
        {
            title: "Batches Remaining", dataIndex: "count", render: styledRow
        },
        {
            title: "Completion", key: "completion", render: (_, rec) => styledRow(`${((rec.pulledItems / rec.totalItems) * 100).toFixed(1)}%`)
        }
    ]

    const subColumns: ColumnsType<ShipmentBatch> = [
        {
            title: "Name",
            dataIndex: "name",
        },
        {
            title: "Generated",
            dataIndex: "generated",
            render: localizedDate
        },
        {
            title: "Type",
            dataIndex: "type",
            render: (t: ShipmentBatchType) => ShipmentBatchTypeNames[t]
        },
        {
            title: "Orders",
            dataIndex: "orders",
        },
        {
            title: "Items",
            dataIndex: "items",
            render: (value, rec) => <Space>
                {value}
                {rec.hasShipmentsWithMissingCards && <Button
                    type="link"
                    icon={<StatusIcon status={ShipmentCardStatus.NotPulled} />}
                    href={StaffRoutes.shipmentBatchMissingCards.url(rec.id)}
                />}
            </Space>
        },
        {
            title: "Pulled",
            dataIndex: "pulled",
            render: (_, rec) => `${((rec.pulled / rec.items) * 100).toFixed(2)}%`
        }
    ];

    return <Table
        id="shipment-batches-table"
        key={data.length}
        rowKey={r => r.id}
        columns={columns}
        dataSource={data}
        loading={isLoading || isUpdating}
        pagination={{
            pageSize: 100,
            hideOnSinglePage: true
        }}
        expandable={{
            defaultExpandAllRows: true,
            defaultExpandedRowKeys: data.map(u => u.id),
            rowExpandable: _ => true,
            expandRowByClick: true,
            expandedRowRender: r => (
                <SortableTable
                    size="small"
                    allowReorder={!isLoading && !isUpdating}
                    onReorder={async (shipmentBatches) => {
                        await reorderAssignments({
                            userId: r.id,
                            shipmentBatchIds: shipmentBatches
                                .map((sb, idx) => ({ [sb.id]: idx }))
                                .reduce((prev, cur) => Object.assign(prev, cur), {})
                        });
                        await refetch();
                    }}
                    columns={subColumns}
                    data={r.shipmentBatches}
                    pagination={{ pageSize: 100, hideOnSinglePage: true }}
                    loading={isUpdating}
                />
            )
        }}
        locale={{ emptyText: <Empty description="There are no active and assigned shipment batches." /> }}
    />;
});

const ShipmentBatchPullingAssignment = () => {
    const [view, setView] = useState(ViewByBatch);
    const ref = useRef<BatchTableRef>(null);

    return (
        <Box p={50}>
            <VStack>
                <Box justifyContent='space-between' display='flex' style={{ width: '100%' }}>

                    <Space direction="vertical">
                        <Box fontSize={36}>Shipment Batch Pulling</Box>
                        <Button type="link" href={StaffRoutes.shipmentBatches.url}>
                            &#8592;Back to Shipment Batches
                        </Button>
                    </Space>

                    <Space direction="horizontal">
                        <BatchAssignmentModal onClose={async (assignmentsMade: boolean) => {
                            if (assignmentsMade) {
                                await ref.current?.refresh();
                            }
                        }} />
                        <Radio.Group
                            size='large'
                            value={view}
                            buttonStyle='solid'
                            onChange={(e) => setView(e.target.value)}
                            style={{ alignSelf: 'end' }}>
                            <Radio.Button value={ViewByBatch}>View By Batch</Radio.Button>
                            <Radio.Button value={ViewByPuller}>View By Puller</Radio.Button>
                        </Radio.Group>
                    </Space>

                </Box>
                <Box w='100%'>
                    {view === ViewByBatch && <ByBatchesTable ref={ref} />}

                    {view === ViewByPuller && <ByPullerTable ref={ref} />}
                </Box>
            </VStack>
        </Box>
    )
}

export default ShipmentBatchPullingAssignment;